import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ExpenseReportFilters {
  subsidiary_ids?: (number | string)[];
  vendor_id?: number | string;
  account_id?: number | string;
  location_id?: number | string;
  project_id?: number | string;
  line_of_business_id?: number | string;
  from_date?: string; // YYYY-MM-DD
  to_date?: string; // YYYY-MM-DD
  expense_type?: 'cogs' | 'fixed_assets' | 'purchase' | 'general_expenses' | '';
  limit?: number;
  offset?: number;
  exclude_void?: boolean;
}

export interface ExpenseReportRow {
  company_name: string;
  gl_code: string;
  gl_name: string;
  transaction_type: string;
  transaction_date: string;
  invoice_no: string;
  invoice_date: string;
  vendor_customer_code: string;
  name: string;
  pan_no: string;
  gst_no: string;
  amount: number;
  memo: string;
  narration: string;
  tds_sec: string;
  tds_rate: number;
  tds_amt: number;
  gst_rate: number;
  gst_amt: number;
  location_name: string;
  line_of_business: string;
  product_category: string;
  product_name: string;
  project_name: string;
  document_number: string;
  vendor_invoice_id: number;
  vendor_invoice_item_id: number;
  journal_entry_id: number;
}

export interface ExpenseReportSummary {
  cogs_total: number;
  fixed_assets_total: number;
  purchase_total: number;
  general_expense_total: number;
}

export interface ExpenseReportResponse {
  status: string;
  message: string;
  data: {
    rows: ExpenseReportRow[];
    total_rows: number;
    total_pages: number;
    total_amount: number;
    total_tds: number;
    total_gst: number;
    summary: ExpenseReportSummary;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ExpenseReportService {
  private readonly API_BASE_URL = environment.accountingUrl;
  private http = inject(HttpClient);

  /**
   * Get expense-wise report with optional filters
   * Endpoint: GET /api/v1/reports/expense
   * 
   * This report covers: COGS, Fixed Assets, Purchase, and General Expenses
   */
  getExpenseReport(filters: ExpenseReportFilters): Observable<ExpenseReportResponse> {
    let params = new HttpParams();

    if (filters.subsidiary_ids && filters.subsidiary_ids.length) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    if (filters.vendor_id !== undefined && filters.vendor_id !== null) {
      params = params.set('vendor_id', filters.vendor_id.toString());
    }

    if (filters.account_id !== undefined && filters.account_id !== null) {
      params = params.set('account_id', filters.account_id.toString());
    }

    if (filters.location_id !== undefined && filters.location_id !== null) {
      params = params.set('location_id', filters.location_id.toString());
    }

    if (filters.project_id !== undefined && filters.project_id !== null) {
      params = params.set('project_id', filters.project_id.toString());
    }

    if (filters.line_of_business_id !== undefined && filters.line_of_business_id !== null) {
      params = params.set('line_of_business_id', filters.line_of_business_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.expense_type) {
      params = params.set('expense_type', filters.expense_type);
    }

    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get<ExpenseReportResponse>(`${this.API_BASE_URL}/api/v1/reports/expense`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Export expense report to Excel
   * Endpoint: GET /api/v1/reports/expense/export?format=xlsx
   */
  exportToExcel(filters: ExpenseReportFilters): Observable<Blob> {
    let params = new HttpParams().set('format', 'xlsx');

   if (filters.subsidiary_ids && filters.subsidiary_ids.length) {
    filters.subsidiary_ids.forEach(id => {
      params = params.append('subsidiary_ids', id.toString());
    });
  }

    if (filters.vendor_id !== undefined && filters.vendor_id !== null) {
      params = params.set('vendor_id', filters.vendor_id.toString());
    }

    if (filters.account_id !== undefined && filters.account_id !== null) {
      params = params.set('account_id', filters.account_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.expense_type) {
      params = params.set('expense_type', filters.expense_type);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get(`${this.API_BASE_URL}/api/v1/reports/expense/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }

  /**
   * Export expense report to PDF
   * Endpoint: GET /api/v1/reports/expense/export?format=pdf
   */
  exportToPdf(filters: ExpenseReportFilters): Observable<Blob> {
    let params = new HttpParams().set('format', 'pdf');

    if (filters.subsidiary_ids && filters.subsidiary_ids.length) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    if (filters.vendor_id !== undefined && filters.vendor_id !== null) {
      params = params.set('vendor_id', filters.vendor_id.toString());
    }

    if (filters.account_id !== undefined && filters.account_id !== null) {
      params = params.set('account_id', filters.account_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.expense_type) {
      params = params.set('expense_type', filters.expense_type);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get(`${this.API_BASE_URL}/api/v1/reports/expense/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }
}

